import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Search, Loader2, BookUser, Check, Star } from "lucide-react";
import { toast } from "sonner";

export default function RubricaDialog({ open, onOpenChange, onConfirm, targetLabel }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState({}); // id -> address

  useEffect(() => {
    if (!open) return;
    setQuery(""); setSelected({});
    setLoading(true);
    api.get("/studio/rubrica")
      .then(({ data }) => setCategories(data.categories || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((c) => ({
        ...c,
        entries: c.entries.filter((e) =>
          [e.name, e.address, e.kind, c.label].some((v) => (v || "").toLowerCase().includes(q))
        ),
      }))
      .filter((c) => c.entries.length > 0);
  }, [categories, query]);

  const toggle = (e) => setSelected((s) => {
    const n = { ...s };
    if (n[e.id]) delete n[e.id]; else n[e.id] = e.address;
    return n;
  });

  const selCount = Object.keys(selected).length;
  const confirm = () => {
    const addrs = Array.from(new Set(Object.values(selected)));
    if (!addrs.length) { toast.error("Seleziona almeno un contatto"); return; }
    onConfirm(addrs);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border text-popover-foreground max-w-2xl max-h-[85vh] flex flex-col" data-testid="rubrica-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <BookUser className="h-5 w-5 text-amber-600" /> Rubrica {targetLabel && <span className="text-xs font-normal text-muted-foreground">→ {targetLabel}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per nome, ragione sociale o email…" data-testid="rubrica-search" className="pl-9 bg-background border-border" />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 min-h-[240px]">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="rubrica-empty">Nessun contatto con email o PEC trovato.</p>
          ) : (
            filtered.map((c) => (
              <div key={c.key} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-amber-600 font-mono font-bold mb-1 sticky top-0 bg-popover py-1">{c.label}</div>
                <div className="space-y-1">
                  {c.entries.map((e) => {
                    const on = !!selected[e.id];
                    return (
                      <button key={e.id} type="button" onClick={() => toggle(e)} data-testid={`rubrica-entry-${e.id}`}
                        className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent"}`}>
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-amber-500 border-amber-500" : "border-muted-foreground/50"}`}>
                          {on && <Check className="h-3 w-3 text-slate-900" />}
                        </span>
                        <span className="text-sm text-foreground truncate flex-1 min-w-0">{e.name}</span>
                        {e.preferito && <Star className="h-3.5 w-3.5 shrink-0 text-amber-500" fill="currentColor" aria-label="Preferito" data-testid={`rubrica-pref-${e.id}`} />}
                        <Badge className={`shrink-0 text-[9px] ${e.kind === "PEC" ? "bg-cyan-500/15 text-cyan-600 border-cyan-500/30" : "bg-slate-500/15 text-muted-foreground border-slate-500/30"}`}>{e.kind}</Badge>
                        <span className="text-xs text-muted-foreground truncate max-w-[240px]">{e.address}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="border-t border-border pt-3">
          <div className="flex items-center gap-3 w-full">
            <span className="text-xs text-muted-foreground" data-testid="rubrica-selected-count">{selCount} selezionati</span>
            <Button onClick={confirm} data-testid="rubrica-confirm" className="ml-auto bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              Aggiungi destinatari
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
